
import { useState, useEffect, useCallback, useRef } from 'react';
import { modelsAPI, type CatalogModel, type ModelCatalogQuery } from '@/lib/api/models';

const PAGE_SIZE = 24;

interface UseModelCatalogReturn {
  models: CatalogModel[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  totalCount: number
  error: string | null
  loadMore: () => void
  refetch: (query?: ModelCatalogQuery) => void
}

interface UseModelCatalogOptions {
  initialQuery?: ModelCatalogQuery
  skipInitialFetch?: boolean
}

export function useModelCatalog(options?: UseModelCatalogOptions): UseModelCatalogReturn {
  const { initialQuery, skipInitialFetch = false } = options ?? {};
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(!skipInitialFetch);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryRef = useRef<ModelCatalogQuery | undefined>(initialQuery);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(async (
    query: ModelCatalogQuery | undefined,
    pageNum: number,
    append: boolean,
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      setError(null);
      const result = await modelsAPI.getCatalog({ ...query, page: pageNum, limit: PAGE_SIZE });
      if (!mountedRef.current) return;
      setModels((prev) => {
        if (!append) return result.data;
        const existingIds = new Set(prev.map(m => m.id));
        const newModels = result.data.filter(m => !existingIds.has(m.id));
        return newModels.length > 0 ? [...prev, ...newModels] : prev;
      });
      setTotalCount(result.meta.total);
      setPage(pageNum);
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load models');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!skipInitialFetch) {
      void doFetch(queryRef.current, 1, false);
    }
  }, [doFetch, skipInitialFetch]);

  const loadMore = useCallback(() => {
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    if (page < totalPages && !isLoadingMore) {
      void doFetch(queryRef.current, page + 1, true);
    }
  }, [page, totalCount, isLoadingMore, doFetch]);

  const refetch = useCallback((query?: ModelCatalogQuery) => {
    queryRef.current = query;
    void doFetch(query, 1, false);
  }, [doFetch]);

  return {
    models,
    isLoading,
    isLoadingMore,
    hasMore: page < Math.ceil(totalCount / PAGE_SIZE),
    totalCount,
    error,
    loadMore,
    refetch,
  };
}
